import './HouseDetailPage.css';
import { useEffect, useState, useMemo } from 'react';
import { useHouseSystem } from './hooks/useHouseSystem';

// Import house system images (fallback only)
import houseSystemCourage from './assets/HouseSystem_Courage.png';
import houseSystemFaith from './assets/HouseSystem_Faith.png';
import houseSystemHope from './assets/HouseSystem_Hope.png';
import houseSystemLove from './assets/HouseSystem_Love.png';
import houseSystemPeace from './assets/HouseSystem_Peace.png';

function HouseDetailPage({ houseName, onNavigate }) {
  const { houseSystemData, isLoading } = useHouseSystem();
  const [mediaMap, setMediaMap] = useState({});

  // Load media map from API
  useEffect(() => {
    const loadMediaMap = async () => {
      try {
        const apiBase = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
        const response = await fetch(
          `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
          }
        );
        
        if (response.ok) {
          const result = await response.json();
          if (result.ok && Array.isArray(result.data)) {
            const map = {};
            result.data.forEach((media) => {
              const mediaId = media.id || media.media_id;
              const mediaUrl = media.url || media.file_path || media.filepath || '';
              if (mediaId && mediaUrl) {
                map[mediaId] = mediaUrl;
                map[String(mediaId)] = mediaUrl;
                map[parseInt(mediaId)] = mediaUrl;
              }
            });
            setMediaMap(map);
          }
        }
      } catch (err) {
        console.error('[HouseDetailPage] Media fetch error:', err);
      }
    };
    loadMediaMap();
  }, []);

  // Auto scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [houseName]);

  // Build house data from CMS, with fallback to hardcoded data
  const houseData = useMemo(() => {
    if (houseSystemData?.houses && Array.isArray(houseSystemData.houses)) {
      const houses = {};
      houseSystemData.houses.forEach((house) => {
        const key = `house-${house.name.toLowerCase().replace(/\s+/g, '-')}`;
        houses[key] = {
          ...house,
          image: mediaMap[house.images?.[0]] || houseSystemCourage,
          icon: 'fa-shield',
          colorLight: house.color ? house.color + '22' : '#fee2e2',
          achievements: Array.isArray(house.achievements) ? house.achievements : [],
          values: Array.isArray(house.values) ? house.values : [],
          activities: Array.isArray(house.activities) ? house.activities : [],
          highlights: house.highlights || house.description || ''
        };
      });
      return houses;
    }

    // Fallback to hardcoded data
    return {
      'house-house-of-courage': {
        name: 'House of Courage',
        motto: 'Courage Unlocks Dreams',
        color: '#dc2626',
        colorLight: '#fee2e2',
        icon: 'fa-shield',
        image: houseSystemCourage,
        description: 'House of Courage embodies the spirit of bravery, determination, and fearless leadership.',
        values: ['Brave leadership', 'Fearless determination', 'Resilience and strength'],
        achievements: ['Leadership Excellence Award 2024'],
        members: 'Approximately 180 students',
        houseLeader: 'Mr. James Wilson',
        activities: ['Leadership development programs'],
        highlights: 'House of Courage fosters bold leaders.'
      },
      'house-house-of-love': {
        name: 'House of Love',
        motto: 'Love Guides Us Forward',
        color: '#ec4899',
        colorLight: '#fce7f3',
        icon: 'fa-heart',
        image: houseSystemLove,
        description: 'House of Love stands for compassion, kindness, and care.',
        values: ['Compassion and empathy', 'Genuine care', 'Inclusivity'],
        achievements: ['Community Service Excellence 2024'],
        members: 'Approximately 175 students',
        houseLeader: 'Ms. Sarah Chen',
        activities: ['Community service projects'],
        highlights: 'House of Love creates a welcoming community.'
      },
      'house-house-of-hope': {
        name: 'House of Hope',
        motto: 'Hope Lights the Way',
        color: '#eab308',
        colorLight: '#fef3c7',
        icon: 'fa-lightbulb',
        image: houseSystemHope,
        description: 'House of Hope inspires optimism, positivity, and vision.',
        values: ['Optimism and positivity', 'Vision for the future', 'Inspiring hope'],
        achievements: ['Vision and Innovation Award 2024'],
        members: 'Approximately 180 students',
        houseLeader: 'Mr. David Martinez',
        activities: ['Vision and goal-setting workshops'],
        highlights: 'House of Hope is the beacon of optimism.'
      },
      'house-house-of-faith': {
        name: 'House of Faith',
        motto: 'Faith Guides Our Path',
        color: '#f97316',
        colorLight: '#ffedd5',
        icon: 'fa-cross',
        image: houseSystemFaith,
        description: 'House of Faith is built on trust and commitment.',
        values: ['Trust and integrity', 'Loyalty and commitment', 'Steadfast values'],
        achievements: ['Character Excellence Award 2024'],
        members: 'Approximately 170 students',
        houseLeader: 'Ms. Emily Rodriguez',
        activities: ['Character development programs'],
        highlights: 'House of Faith cultivates trust and integrity.'
      },
      'house-house-of-peace': {
        name: 'House of Peace',
        motto: 'Peace Leads Us Forward',
        color: '#22c55e',
        colorLight: '#dcfce7',
        icon: 'fa-dove',
        image: houseSystemPeace,
        description: 'House of Peace promotes harmony and collaboration.',
        values: ['Harmony and balance', 'Understanding and empathy', 'Peaceful collaboration'],
        achievements: ['Peace and Harmony Award 2024'],
        members: 'Approximately 175 students',
        houseLeader: 'Mr. Christopher Lee',
        activities: ['Conflict resolution workshops'],
        highlights: 'House of Peace fosters harmony and understanding.'
      }
    };
  }, [houseSystemData, mediaMap]);

  if (isLoading) {
    return (
      <div className="house-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading house details...</p>
      </div>
    );
  }

  const house = houseData[houseName];

  if (!house) {
    return (
      <div className="house-detail-error">
        <h2>House not found</h2>
        <p>The house you're looking for doesn't exist.</p>
        <button onClick={() => {
          onNavigate('student-life');
          setTimeout(() => {
            const element = document.getElementById('house-system');
            if (element) element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }}>Back to Student Life</button>
      </div>
    );
  }

  return (
    <section className="house-detail-page">
      {/* Hero Section with Image */}
      <section className="house-detail-hero" style={{ backgroundColor: house.color }}>
        <div className="house-detail-hero-pattern" />
        <div className="house-detail-hero-image">
          <img src={house.image} alt={house.name} />
          <div className="house-detail-hero-overlay" />
        </div>
        <div className="house-detail-hero-content">
          <button className="house-detail-back-btn" onClick={() => {
            onNavigate('student-life');
            setTimeout(() => {
              const element = document.getElementById('house-system');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }}>
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="house-detail-hero-icon">
            <i className={`fas ${house.icon}`} />
          </div>
          <h1 className="house-detail-hero-title">{house.name}</h1>
          <p className="house-detail-hero-motto">"{house.motto}"</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="house-detail-main">
        {/* About Section */}
        <div className="house-detail-section">
          <div className="house-detail-section-header">
            <h2>About {house.name}</h2>
            <div className="house-detail-section-line" style={{ backgroundColor: house.color }} />
          </div>
          <p className="house-detail-description">{house.description}</p>
          <div className="house-detail-highlight-box" style={{ backgroundColor: house.colorLight, borderLeftColor: house.color }}>
            <i className={`fas ${house.icon}`} style={{ color: house.color }} />
            <p>{house.highlights}</p>
          </div>
        </div>

        {/* Quick Info Cards */}
        <div className="house-detail-info-grid">
          <div className="house-detail-info-card">
            <div className="house-detail-info-icon" style={{ backgroundColor: house.colorLight, color: house.color }}>
              <i className="fas fa-users" />
            </div>
            <h4>Members</h4>
            <p>{house.members}</p>
          </div>
          <div className="house-detail-info-card">
            <div className="house-detail-info-icon" style={{ backgroundColor: house.colorLight, color: house.color }}>
              <i className="fas fa-user-tie" />
            </div>
            <h4>House Leader</h4>
            <p>{house.houseLeader}</p>
          </div>
          <div className="house-detail-info-card">
            <div className="house-detail-info-icon" style={{ backgroundColor: house.colorLight, color: house.color }}>
              <i className="fas fa-star" />
            </div>
            <h4>Core Values</h4>
            <p>{house.values.length} Values</p>
          </div>
        </div>

        {/* Core Values Section */}
        {house.values.length > 0 && (
          <div className="house-detail-section">
            <div className="house-detail-section-header">
              <h2>Core Values</h2>
              <div className="house-detail-section-line" style={{ backgroundColor: house.color }} />
            </div>
            <div className="house-detail-values-grid">
              {house.values.map((value, index) => (
                <div key={value} className="house-detail-value-card" style={{ borderTopColor: house.color }}>
                  <div className="house-detail-value-badge" style={{ backgroundColor: house.color }}>
                    {index + 1}
                  </div>
                  <h4>{value}</h4>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities Section */}
        {house.activities.length > 0 && (
          <div className="house-detail-section">
            <div className="house-detail-section-header">
              <h2>House Activities</h2>
              <div className="house-detail-section-line" style={{ backgroundColor: house.color }} />
            </div>
            <div className="house-detail-activities-grid">
              {house.activities.map((activity, index) => (
                <div key={activity} className="house-detail-activity-card">
                  <div className="house-detail-activity-number" style={{ backgroundColor: house.color }}>
                    {index + 1}
                  </div>
                  <div className="house-detail-activity-content">
                    <p>{activity}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Achievements Section */}
        {house.achievements.length > 0 && (
          <div className="house-detail-section">
            <div className="house-detail-section-header">
              <h2>Achievements & Recognition</h2>
              <div className="house-detail-section-line" style={{ backgroundColor: house.color }} />
            </div>
            <div className="house-detail-achievements-grid">
              {house.achievements.map((achievement) => (
                <div key={achievement} className="house-detail-achievement-card" style={{ backgroundColor: house.colorLight, borderLeftColor: house.color }}>
                  <div className="house-detail-achievement-icon" style={{ color: house.color }}>
                    <i className="fas fa-trophy" />
                  </div>
                  <p>{achievement}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="house-detail-cta" style={{ backgroundColor: house.color }}>
        <div className="house-detail-cta-content">
          <h2>Join {house.name}</h2>
          <p>Be part of a community that values {house.motto.toLowerCase()}</p>
          <button className="house-detail-cta-btn" onClick={() => {
            onNavigate('student-life');
            setTimeout(() => {
              const element = document.getElementById('house-system');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }}>
            Explore Other Houses <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default HouseDetailPage;
